import React, { useState, useEffect } from 'react';
import { MotionConfig, motion } from 'framer-motion';
import ConversacionesWhatsApp from './ConversacionesWhatsApp';
import AsistenteWhatsApp from './AsistenteWhatsApp';
import QueContestaWhatsApp from './QueContestaWhatsApp';
import { anima, DURACION, CURVA } from './movimientoWhatsApp';
import './SeccionWhatsApp.css';

/**
 * La sección de WhatsApp, en pestañas.
 *
 * Estaba todo una cosa debajo de la otra y era demasiado para una sola
 * pantalla: para llegar al estado del asistente había que pasar por encima de
 * toda la lista de conversaciones y el hilo abierto.
 *
 * Son tres cosas de frecuencia muy distinta, y ese es el orden:
 *
 *   Conversaciones       varias veces por día
 *   Qué contesta         cuando el bot no supo algo, o al configurar
 *   Estado del asistente una vez, y cuando algo falla
 */

const SeccionWhatsApp = () => {
  const [pestania, setPestania] = useState('conversaciones');
  // Qué se está MOSTRANDO, que no es lo mismo que qué se eligió: entre las dos
  // cosas hay una salida que dura unos milisegundos. `pestania` cambia apenas
  // hacés clic —la solapa se marca al toque, que es lo que confirma que te
  // escuchó— y `visible` cambia recién cuando la anterior terminó de irse.
  const [visible, setVisible] = useState('conversaciones');
  const [saliendo, setSaliendo] = useState(false);

  // Salir y entrar, uno después del otro y no encimados. Encimarlos pedía
  // sacar los paneles del flujo con posición absoluta, y el de conversaciones
  // mide su alto contra la ventana: se le rompe el alto y con él el scroll del
  // hilo. Un cruce lindo no vale una pantalla rota.
  useEffect(() => {
    if (pestania === visible) return undefined;
    setSaliendo(true);
    const id = setTimeout(() => {
      setVisible(pestania);
      setSaliendo(false);
    }, DURACION.salida * 1000);
    return () => clearTimeout(id);
  }, [pestania, visible]);

  const pestanias = [
    { id: 'conversaciones', etiqueta: 'Conversaciones' },
    { id: 'contesta', etiqueta: 'Qué contesta' },
    { id: 'asistente', etiqueta: 'Estado del asistente' },
  ];

  return (
    // `reducedMotion="user"` respeta el "reducir movimiento" del sistema para
    // TODO lo que se anime acá adentro, sin que cada componente tenga que
    // preguntarlo por su cuenta. Con la opción puesta, framer deja quietos los
    // desplazamientos y conserva los desvanecidos, que es la recomendación:
    // lo que marea es el movimiento, no el cambio de opacidad.
    <MotionConfig reducedMotion="user">
    <div className="wa-seccion">
      {/* `role="tablist"` y las flechas del teclado no son decoración: sin
          eso, esto es un par de botones que un lector de pantalla anuncia
          sueltos, sin decir que son alternativas de la misma zona. */}
      <div className="wa-seccion__pestanias" role="tablist" aria-label="Asistente de WhatsApp">
        {pestanias.map((p, i) => (
          <button
            key={p.id}
            role="tab"
            id={`wa-tab-${p.id}`}
            aria-selected={pestania === p.id}
            aria-controls={`wa-panel-${p.id}`}
            tabIndex={pestania === p.id ? 0 : -1}
            className={`wa-seccion__pestania ${pestania === p.id ? 'es-activa' : ''}`}
            onClick={() => setPestania(p.id)}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
              const paso = e.key === 'ArrowRight' ? 1 : -1;
              const siguiente = pestanias[(i + paso + pestanias.length) % pestanias.length];
              setPestania(siguiente.id);
              document.getElementById(`wa-tab-${siguiente.id}`)?.focus();
            }}
          >
            {p.etiqueta}
          </button>
        ))}
      </div>

      <motion.div
        {...anima}
        animate={{ opacity: saliendo ? 0 : 1, '--wa-y': saliendo ? '6px' : '0px' }}
        transition={{
          duration: saliendo ? DURACION.salida : DURACION.entrada,
          ease: CURVA,
        }}
      >
      {/* Las dos quedan MONTADAS, solo se esconde la que no está activa.
          Desmontar la de conversaciones cortaría su refresco: alguien que
          mira el estado del asistente treinta segundos volvería sin haberse
          enterado de que le escribieron, y sin que hubiera sonado nada. */}
      <div
        role="tabpanel"
        id="wa-panel-conversaciones"
        aria-labelledby="wa-tab-conversaciones"
        hidden={visible !== 'conversaciones'}
      >
        <ConversacionesWhatsApp />
      </div>

      {/* Esta SÍ se desmonta, al revés que la de conversaciones. No tiene
          nada que refrescar en segundo plano, y montarla de nuevo es lo que
          hace que al volver muestre lo último guardado en vez de un borrador
          de hace media hora. */}
      {visible === 'contesta' && (
        <div
          role="tabpanel"
          id="wa-panel-contesta"
          aria-labelledby="wa-tab-contesta"
        >
          <QueContestaWhatsApp />
        </div>
      )}

      <div
        role="tabpanel"
        id="wa-panel-asistente"
        aria-labelledby="wa-tab-asistente"
        hidden={visible !== 'asistente'}
      >
        <AsistenteWhatsApp />
      </div>
      </motion.div>
    </div>
    </MotionConfig>
  );
};

export default SeccionWhatsApp;
